// Auckland Council Regional Parks booking strategy.
//
// SAP Hybris cart at onlineservices.aucklandcouncil.govt.nz. The bare
// reserveUrl (`addToBooking/{productId}`) redirects through the myAUCKLAND
// login wall at signin.aucklandcouncil.govt.nz; the `__rpurn` return-URL
// state preserves dateFrom / dateTo / adult / child / infant / pax through
// login. See agencies/AucklandCouncil/proof/discovery.md
// §"Reserve URL verification".
//
// The cart branches on the product's `perPersonPrice` flag (visible in the
// OCC product detail; mirrored in our catalog as accommodationType):
//
//   - Camping (perPersonPrice = true) → `adult=N&child=0&infant=0` only;
//     party size decrements per-person inventory at the cart layer.
//   - Everything else (Bach / Tiny home / Lodge / Glamping / Tent / CSC
//     Parking / CSC Overnight) is `perPersonPrice = false` — the cart
//     REJECTS adult/child/infant > 0 with the error "Adult, Child and Infant
//     fields can not be specified for this product. Product is not configured
//     per person price." and hides the Continue button. Use `pax=N` instead,
//     with adult/child/infant zeroed. CSC sites are one vehicle per booking
//     so pax is fixed at 1 regardless of party size.
//
// Verified live by walking the booking flow on aucklandcouncil.govt.nz with
// Camping (220233), Bach (220230), and CSC parking (220014).
//
// Date format is ISO-like with hard-coded check-in (14:00:00) and check-out
// (13:59:00) clock times. The cart parses just the date portion; the
// wall-clock suffix is ignored. The SPA itself emits T08:00:00 / T07:59:00
// but both work — we keep our 14:00 / 13:59 anchors because they better
// reflect the documented per-product check-in (10–11am) and check-out
// (10–11am) wall-clock semantics.

import { resolveCheckout, resolveMinCapacity } from "./_context.js";

const CSC_TYPES = new Set(["CSC Parking area", "CSC Overnight parking area"]);

function formatHybrisDate(date, time) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${time}`;
}

export default {
  buildBookingUrl(ctx) {
    const { site, arrivalDate } = ctx;
    const checkout = resolveCheckout(ctx);
    const partySize = resolveMinCapacity(ctx);
    const accommodationType = site.extraAttributes?.accommodationType;

    const url = new URL(site.reserveUrl);
    url.searchParams.set("dateFrom", formatHybrisDate(arrivalDate, "14:00:00"));
    url.searchParams.set("dateTo", formatHybrisDate(checkout, "13:59:00"));

    if (accommodationType === "Camping") {
      url.searchParams.set("adult", String(partySize));
      url.searchParams.set("child", "0");
      url.searchParams.set("infant", "0");
    } else {
      const pax = CSC_TYPES.has(accommodationType) ? 1 : partySize;
      url.searchParams.set("adult", "0");
      url.searchParams.set("child", "0");
      url.searchParams.set("infant", "0");
      url.searchParams.set("pax", String(pax));
    }
    return url.toString();
  },

  buildPreviewUrl() {
    // No preview URL — the request form's "View map" alert is hidden for
    // Auckland Council. The bare reserveUrl redirects to the myAUCKLAND
    // login HRD (signin.aucklandcouncil.govt.nz), which is a useless
    // preview. Auckland Council's actual marketing pages
    // (www.aucklandcouncil.govt.nz/.../park-detail/{id}.html and
    // /find-accommodation/accommodation-details/{id}.html) are
    // geo-restricted to Oceania ASNs and return HTTP 406 to non-NZ
    // visitors — Schnerp users browsing from anywhere else would land
    // on an error page. Until a non-geo-restricted marketing surface
    // exists, we hide the alert.
    return null;
  },

  describeBookingInputs() {
    return {
      inputs: {
        arrivalDate: {
          status: "required",
          notes: "Encoded as &dateFrom=<ISO>T14:00:00.",
        },
        departureDate: {
          status: "required",
          notes:
            "Encoded as &dateTo=<ISO>T13:59:00; resolved from arrival + minStayLength when not supplied.",
        },
        ref: {
          status: "ignored",
          notes:
            "site.reserveUrl already encodes the SAP Hybris productId path; ref itself is not propagated.",
        },
        partySize: {
          status: "encoded",
          defaultValue: 1,
          notes:
            "Encoding branches on accommodationType: Camping (perPersonPrice=true) → &adult=<N>&child=0&infant=0. Everything else (Bach/Tiny home/Lodge/Glamping/Tent and both CSC types — perPersonPrice=false) → &pax=<N>&adult=0&child=0&infant=0; CSC sites override pax to 1 (one vehicle per booking).",
        },
      },
    };
  },
};
